import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Row, Col } from 'react-bootstrap';
import Slider from 'react-rangeslider';
import { connect } from 'react-redux';
import s from './Network.css';

import GeneNetwork from '../GeneNetwork';
import NetworkTabs from '../NetworkTabs';
import Tooltip from '../Tooltip';
import Tutorial from '../Tutorial';

import { fetchTermPredictions } from '../../actions/PredictionActions';

import {
  selectEdge,
  selectNode,
  fetchEvidence,
  fetchMultiNetworkEdge,
  removeGeneset,
} from '../../actions/NetworkActions';

/*
 * Presentational component of a network. Shows a gene network, enriched terms,
 * top connected genes, and detailed views if an edge or node is selected
 * by the user,
 */

class NetworkView extends Component {
  static propTypes = {
    // Identifer for keeping track of network in global state store
    // {group, idx}
    id: PropTypes.object.isRequired,

    // Queried genes
    genes: PropTypes.array.isRequired,

    // Integration context
    integration: PropTypes.object.isRequired,

    // Data of vertices and edges corresponding to the network
    data: PropTypes.object.isRequired,

    // Default cutoffs to be applied to the network
    edgeCutoff: PropTypes.number,
    nodeCutoff: PropTypes.number,

    // Display vertically or horizontally
    vertical: PropTypes.bool,

    // Callback after gene network is created
    onInitNetwork: PropTypes.func,

    // Callback when network is filtered (gene is added or removed)
    onNetworkFilter: PropTypes.func,

    // Callback when an edge is clicked
    onEdgeClickAllNetworks: PropTypes.func,

    // Callback when a gene is selected
    onGeneSelect: PropTypes.func,

    // Callback when an edge cutoff is set
    onEdgeCutoff: PropTypes.func,

    // Callback when a node cutoff is set
    onNodeCutoff: PropTypes.func,

    // Callback when a term is de-selected
    onTermDeselect: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      visibleGenes: [],
      edgeCutoff: this.props.edgeCutoff,
      nodeCutoff: this.props.nodeCutoff,
      degreeCorrect: false,
      expandView: false,
    };
    this._genes = [];
    this.svgRef = React.createRef();
    this.handleNetworkFilter = this.handleNetworkFilter.bind(this);
    this.updateGenePredictions = this.updateGenePredictions.bind(this);
  }

  static defaultProps = {
    edgeCutoff: 0.5,
    nodeCutoff: 10,
    vertical: true,
    columns: 1,
  };

  handleNetworkFilter(genesadd, genesrm, genes) {
    if (genesadd[0].length || genesrm[0].length) {
      this.setState({ visibleGenes: genes });
    }
    if (this.props.onNetworkFilter)
      this.props.onNetworkFilter(genesadd, genesrm, genes);
  }

  updateGenePredictions(data) {
    // Calculate network gene connectivity scores
    this._genes = data.genes
      .slice(0)
      .filter(g => g.query_degree > 0)
      .sort((a, b) => b.query_degree - a.query_degree);
    for (const k in this._genes)
      this._genes[k].score = this._genes[k].query_degree;
  }

  render() {
    let networkjs = null;

    // Network d3 visualization
    networkjs = (
      <GeneNetwork
        ref={this.svgRef}
        genes={this.props.data.genes}
        edges={this.props.data.edges}
        edgeCutoff={this.state.edgeCutoff}
        nodeLimit={this.state.nodeCutoff}
        degreeCorrect={this.state.degreeCorrect}
        geneSets={this.props.geneSets}
        title={this.props.integration.context.title}
        onEdgeClick={(d, edges) => {
          this.props.onEdgeClickAllNetworks(d, edges);
        }}
        onNodeClick={d => {
          this.props.onGeneSelect(d, this.props.termDatabases);
        }}
        onNetworkFilter={this.handleNetworkFilter}
        onCreate={network => {
          if (this.props.onInitNetwork) this.props.onInitNetwork(network);
          this.updateGenePredictions(this.props.data);
        }}
        onGeneSetRemove={this.props.onTermDeselect}
        onExpandView={expand => {
          this.setState({ expandView: expand });
        }}
      />
    );

    // Network sliders for filtering genes and edges
    const networksliders = (
      // Change the component key when the sliders require remounting
      <div
        ref={e => (this._networkSliders = e)}
        key={this.state.expandView ? 'expand-slider' : 'slider'}
      >
        <Tooltip
          text={`Edges with weights greater than ${this.state.edgeCutoff.toFixed(
            2,
          )} are displayed`}
        >
          <span>
            Mimimum interaction confidence: {this.state.edgeCutoff.toFixed(2)}
          </span>
          <Slider
            min={0}
            max={1}
            step={0.01}
            value={this.state.edgeCutoff}
            tooltip={false}
            onChange={edgeCutoff => this.setState({ edgeCutoff })}
          />
        </Tooltip>
        <Tooltip
          text={
            `The maximum number of genes displayed is ${this.state.nodeCutoff} (not including the query genes). Genes are prioritized by how highly ` +
            `connected they are to the queried genes.`
          }
        >
          <span>Maximum number of genes: {this.state.nodeCutoff}</span>
          <Slider
            min={0}
            max={50}
            step={1}
            value={this.state.nodeCutoff}
            tooltip={false}
            onChange={nodeCutoff => this.setState({ nodeCutoff })}
          />
        </Tooltip>
        <div className="switch_box">
          <input
            id="uniqueness"
            type="checkbox"
            className="checkbox_switchbox"
            checked={this.state.degreeCorrect && 'true'}
            onChange={event => {
              this.setState({ degreeCorrect: event.target.checked });
            }}
          />
          <Tooltip
            text={
              'Genes will be prioritized by how highly connected they' +
              ' are to the queried genes relative to their connections to non-query genes.'
            }
          >
            <label htmlFor="uniqueness" className={s.label}>
              Prioritize by uniqueness to the query genes
            </label>
          </Tooltip>
        </div>
      </div>
    );

    // Stack the tab below the network
    const tabStacked = this.props.vertical || this.state.expandView;
    // The number of tab columns to use (detail tab will be displayed in the second
    // column)
    const tabCols = this.state.expandView && !this.props.vertical ? 2 : 1;
    // bootstrap columns for stacked vs horizontal layout
    const bsCols = tabStacked ? 12 : 6;

    return (
      <div>
        <Row>
          <Col md={bsCols}>
            <div>
              <h4>
                {this.props.integration.context.title}
                <small>
                  {' '}
                  -{' '}
                  {this.props.integration.context.term &&
                    this.props.integration.context.term.description}
                </small>
              </h4>
            </div>
            {networkjs}
            {networksliders}
            {tabStacked ? (
              <NetworkTabs
                id={this.props.id}
                columns={tabCols}
                genePredictions={this._genes}
                visibleGenes={this.state.visibleGenes}
              />
            ) : null}
          </Col>
          {!tabStacked ? (
            <Col md={bsCols}>
              <NetworkTabs
                id={this.props.id}
                columns={tabCols}
                genePredictions={this._genes}
                visibleGenes={this.state.visibleGenes}
              />
            </Col>
          ) : null}
        </Row>

        <Tutorial
          step={2}
          target={this.svgRef.current}
          container={this}
          title="Tissue-specific interactions"
          text="This is a network of tissue-specific functional interactions with your query genes, predicted by integrating thousands of genome-wide experiments. The nodes represent genes and edges represent the predicted probability that the connected genes participate in the same biological process in the tissue."
        />
        <Tutorial
          step={3}
          title="Adjust the network"
          target={this._networkSliders}
          container={this}
          text="Adjust the network with these sliders. Filter by prediction confidence or limit the network by the number of displayed genes."
        />
      </div>
    );
  }
}

function mapStateToProps(state, props) {
  const { group, idx } = props.id;
  const network = state.networkGroups[group][idx];
  return {
    data: network?.data,
    integration: network?.selectedIntegration,
    genes: network?.selectedGenes,

    geneSets: state.geneSets,

    selectedEdge: network?.selectedEdge,
    selectedGene: network?.selectedNode,

    /* Use component state to store edge and node cutoffs, for now.
     * Global state stores the default cutoffs.
     */
    edgeCutoff: network?.edgeCutoff,
    nodeCutoff: network?.nodeCutoff,

    termDatabases: state.termDatabases,
  };
}

function mapDispatchToProps(dispatch, props) {
  const { group, idx } = props.id;
  return {
    onEdgeClickAllNetworks: (d, allEdges) => {
      // Iterate over matching edges and call dispatch
      for (let id in allEdges) {
        if (!allEdges[id]) continue;
        const edge = allEdges[id];

        id = parseInt(id);

        // Deselect node
        dispatch(selectNode(null, { group, idx: id }));

        // Select edge and retrieve evidence
        dispatch(selectEdge(edge, { group, idx: id }));
        dispatch(fetchMultiNetworkEdge(edge, { group, idx: id }));
        dispatch(fetchEvidence(edge, { group, idx: id }));
      }
    },
    onGeneSelect: (d, termDatabases) => {
      dispatch(selectEdge(null, props.id));
      dispatch(selectNode(d, props.id));
      for (const db in termDatabases) {
        dispatch(fetchTermPredictions(d, termDatabases[db].slug));
      }
    },
    /* Use component state to store edge and node cutoffs, for now.
     * We might want to store these in the global state if need to
     * be accessed in other components
     */
    // onEdgeCutoff: (value) => {
    // dispatch(setEdgeCutoff(value,props.id));
    // },
    // onNodeCutoff: (value) => {
    // dispatch(setNodeCutoff(value,props.id));
    // },
    onTermDeselect: term => {
      dispatch(removeGeneset(term.identifier));
    },
  };
}

const Network = connect(mapStateToProps, mapDispatchToProps)(NetworkView);

export default withStyles(s)(Network);
